import { Component, computed, EventEmitter, Input, Output, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { heroCloud, heroArrowRight } from '@ng-icons/heroicons/outline';
import { phosphorArrowCircleRight, phosphorCheckCircle, phosphorHardDrive, phosphorPlusCircle, phosphorWarning, phosphorXCircle } from '@ng-icons/phosphor-icons/regular';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { environment } from '../../../../environments/environment';
import { OauthService } from '../../../services/oauth.service';
import { Repo, RepoService } from '../../../services/repo.service';
import { ToastService } from '../../../services/toast.service';

type newRepoSelectionState = {
  type: 'local',
  state: 'url_invalid' | 'repo_exists' | 'repo_does_not_exist' | 'repo_created' | 'repo_creation_failed' | 'repo_id_mismatch';
} |
{
  type: 'cloud',
  state: 'repo_selected' | 'new_repo_request' | 'repo_created' | 'repo_creation_failed' | 'repo_exists';
    };

@Component({
  selector: 'app-create-repo',
  imports: [DialogModule, SelectModule, RadioButtonModule, FormsModule, NgIconComponent, FloatLabelModule, InputTextModule, TabsModule],
  templateUrl: './create-repo.component.html',
  styleUrl: './create-repo.component.css',
  providers: [provideIcons({ heroCloud, heroArrowRight, phosphorHardDrive, phosphorArrowCircleRight, phosphorWarning, phosphorPlusCircle, phosphorXCircle, phosphorCheckCircle })]
})
export class CreateRepoComponent {
  @Input() createRepoVisible: boolean = false;
  @Output() selectedRepoIdChange = new EventEmitter<string | null>();
  @Output() createRepoVisibleChange = new EventEmitter<boolean>();

  constructor(private repoService: RepoService, private toast: ToastService, private oauth: OauthService) { }

  repo: Signal<Repo | null> = computed(() => this.repoService.repo());
  repoSelectionState: newRepoSelectionState | null = null;
  selectedRepoType: string = '';
  cloudRepoName: string = '';
  cloudRepos: Repo[] = [];
  selectedRepo: Repo | null = null;
  isConnecting: boolean = false;
  selectedRepoVisible: boolean = false;
  selectedRepoId: string | null = null;

  // Loading messages
  private loadingMessages: string[] = [
    'Creating your workspace',
    'Copying CDISC raw study data',
    'Setting up data model and metadata',
    'Configuring analysis environment',
    'Checking data integrity and consistency',
    'Indexing datasets and caching for performance',
    'Setting up user preferences and permissions',
    'Finalizing workspace setup',
    'Workspace ready!'
  ];
  currentLoadingMessage: string = this.loadingMessages[0];
  private loadingMessageIndex: number = 0;
  private loadingMessageInterval: ReturnType<typeof setInterval> | null = null;

  onCreateRepoDialogShow() {
    this.repoService.getRepos().then((repos: Repo[]) => {
      this.cloudRepos = repos.filter(repo => repo.type.toLowerCase() === 'cloud');
      // console.log('Fetched repos:', repos.length, 'Cloud repos:', this.cloudRepos.length);
    });
  }

  toggleButton(repo: string): void {
    this.selectedRepoType = repo;
  }

  onSelectedRepoDialogShow(repoId: string) {
    this.selectedRepoId = repoId;
    this.selectedRepoVisible = true;
  }

  onClose() {
    this.selectedRepoVisible = false;
    this.createRepoVisible = false;
    this.createRepoVisibleChange.emit(false);
    this.selectedRepoIdChange.emit(null);
  }

  onDialogHide() {
    this.repoSelectionState = null;
    this.selectedRepoType = '';
    this.stopLoadingMessages();
  }

  private startLoadingMessages() {
    this.loadingMessageIndex = 0;
    this.currentLoadingMessage = this.loadingMessages[0];
    
    this.loadingMessageInterval = setInterval(() => {
      this.loadingMessageIndex = (this.loadingMessageIndex + 1) % this.loadingMessages.length;
      this.currentLoadingMessage = this.loadingMessages[this.loadingMessageIndex];
    }, 10000); // 10 seconds interval
  }

  private stopLoadingMessages() {
    if (this.loadingMessageInterval) {
      clearInterval(this.loadingMessageInterval);
      this.loadingMessageInterval = null;
    }
    this.loadingMessageIndex = 0;
    this.currentLoadingMessage = this.loadingMessages[0];
  }

  async connectCloudExistingRepo(repoName: string, repoId: string) {
    // console.log('Connecting to existing cloud repository:', repoName, repoId);
    this.isConnecting = true;
    this.startLoadingMessages();
    try {
      const repo_Id = String(repoId);
      const cloudStatus = await this.repoService.getCloudRepoStatus(repo_Id);
      const repoServerUrl = environment.userMcpMicroserviceUrl + '/' + this.oauth.user?.user_id + '-' + repo_Id;
      if (cloudStatus.returncode !== "0") {
        await this.repoService.startCloudRepo(repo_Id);
      } else {
        const repo_Id = await this.repoService.getIdFromRepo(repoServerUrl);
        if (!repo_Id) {
          await this.repoService.createAssignRepo(repoName, 'cloud', repoServerUrl);
        }
      }
      const repoDetails = await this.repoService.fetchRepoDetails(repo_Id, repoServerUrl);
      this.repoService.setRepo(repoDetails);
      this.toast.showSuccess('Connected to repository successfully.');
      this.createRepoVisible = false;
      // this.repoSelectionState = {
      //   type: 'cloud',
      //   state: 'repo_exists'
      // };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.repoSelectionState = {
        type: 'cloud',
        state: 'repo_creation_failed'
      };
      if (error.cause?.errorType === 'REPO_CREATION_FAILED') {
        console.error('Study creation failed:', error.cause);
        throw new Error('Failed to create study. Please try again.', {
          cause: error.cause
        });
      } else if (error.cause?.errorType === 'REPO_ASSIGNMENT_FAILED') {
        console.error('Study assignment failed:', error.cause);
        throw new Error('Failed to assign study. Please try again.', {
          cause: error.cause
        });
      } else {
        console.error('Error connecting to existing cloud repository:', error);
        throw new Error('Failed to connect to existing cloud repository. Please try again.', {
          cause: error.cause
        });
      }
    } finally {
      this.isConnecting = false;
      this.stopLoadingMessages();
    }
  }

  async newRepo(repoName: string, type: 'local' | 'cloud', serverUrl: string) {
    this.isConnecting = true;
    this.startLoadingMessages();
    if (!repoName) {
      this.toast.showError('Study name cannot be empty.');
      return;
    }
    try {
      await this.repoService.createAssignRepo(repoName, type, serverUrl);
      this.toast.showSuccess('Study created and assigned successfully.');
      this.repoSelectionState = {
        type: type,
        state: 'repo_created'
      };
      sessionStorage.setItem('currentRepoUrl', serverUrl);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Update repo selection state at the end of catch block
      this.repoSelectionState = {
        type: type,
        state: 'repo_creation_failed'
      };
      if (error.cause?.errorType === 'REPO_CREATION_FAILED') {
        console.error('Study creation failed:', error.cause);
        throw new Error('Failed to create study. Please try again.', {
          cause: error.cause
        });
      } else if (error.cause?.errorType === 'REPO_ASSIGNMENT_FAILED') {
        console.error('Study assignment failed:', error.cause);
        throw new Error('Failed to assign study. Please try again.', {
          cause: error.cause
        });
      } else {
        console.error('Unexpected error while creating study:', error);
        throw new Error('An unexpected error occurred while creating the study.', {
          cause: error.cause
        });
      }
    } finally {
      this.isConnecting = false;
      this.stopLoadingMessages();
    }
  }

  async checkServerUrlValidity(url: string) {
    if (!url) {
      this.repoSelectionState = {
        type: 'local',
        state: 'url_invalid',
      };
    }
    const repoId = await this.repoService.getIdFromRepo(url);
    if (this.selectedRepoId !== repoId) {
      this.repoSelectionState = {
        type: 'local',
        state: 'repo_id_mismatch',
      };
      console.log(
        'Repo ID does not match selected repo ID. Fetching repo details...',
      );
    }else {
      await this.localSubmitServerUrl(url);
    }
  }

  async localSubmitServerUrl(url: string) {
    if (!url) {
      this.repoSelectionState = {
        type: 'local',
        state: 'url_invalid'
      };
    }

    sessionStorage.setItem('currentRepoUrl', url);
    try {
      const repoId = await this.repoService.getIdFromRepo(url);

        if (repoId) {
        const repoDetails = await this.repoService.fetchRepoDetails(repoId, url);
          this.repoService.setRepo(repoDetails);
          if (repoDetails) {
            this.toast.showSuccess('Connected to repository successfully.');
            this.selectedRepoVisible = false;
            this.createRepoVisible = false;
          }
        } else {
          this.repoSelectionState = {
            type: 'local',
          state: 'repo_does_not_exist'
          };
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.cause?.details?.includes('0 rows')) {
        this.repoSelectionState = {
          type: 'local',
          state: 'repo_does_not_exist'
        };
      } else if (error.cause?.name === "HttpErrorResponse") {
        this.repoSelectionState = {
          type: 'local',
          state: 'url_invalid'
        };
        throw new Error('Invalid URL or server not reachable.', {
          cause: {
            code: 'REPO_NOT_FOUND'
          }
        });
      } else {
        console.error(error.cause);
        throw new Error('Error fetching repo details: ' + error.message, { cause: error.cause });
      }
    }

    console.log('Repo selection state:', this.repoSelectionState);
  }

  async testRepoCreation() {
    // const resp = await this.repoService.test()
    console.log('Ha ha ha');
  }

}
